/* @flow */

import React from 'react';
import { get, some, values, filter } from 'lodash/fp';
import { connect, useDispatch } from 'react-redux';
import colorUtils from 'color';
import glamorous from 'glamorous';
import numeral from 'numeral';
import styled from '@emotion/styled';

import { toggleChat } from 'app/actions/uiActions';
import { toggleCampaignModal } from 'app/actions/userActions';
import MobileMenuItem from 'app/components/menu/MobileMenuItem';
import t from 'app/utils/translate';
import { IN_PROGRESS } from '../../constants/challengeStatusTypes';
import secondaryLinksSelector from '../../selectors/secondaryLinksSelector';
import { TICKET } from 'app/constants/bonusTypes';

type Props = {
  blogLink: ?string,
  externalLinks: ?Array<*>,
  displayComboCalculator: boolean,
  displayFantasyInsider: boolean,
  displayFriends: boolean,
  displayGlobalLeaderboard: boolean,
  displayGiftStore: boolean,
  displayRapidFire: boolean,
  hasPlayUpCampaign: boolean,
  campaignButtonTitle?: string,
  showBetWithPlayUp: boolean,
  displayMyTeams: boolean,
  displayTraining: boolean,
  giftStoreName: string,
  isSignedIn: boolean,
  markLive: boolean,
  userTotalBonus?: number,
  ticketsCount?: number,
  onClose: () => void,
  onToggleCampaignModal: () => void,
  onTapContentLink: string => void,
  secondaryLinks: Array<Object>
};

const MenuGroup = glamorous.div(({ theme }) => ({
  borderTopColor: colorUtils(theme.color.basis2)
    .whiten(0.3)
    .hex(),
  borderTopStyle: 'solid',
  borderTopWidth: 1,
  '&:not(:last-child)': {
    paddingBottom: theme.spacing.default
  }
}));

function MobileMenuList({
  blogLink,
  externalLinks = [],
  displayComboCalculator,
  displayFantasyInsider,
  displayFriends,
  displayGlobalLeaderboard,
  displayGiftStore,
  displayTraining,
  displayRapidFire,
  displayMyTeams,
  hasPlayUpCampaign,
  giftStoreName,
  isSignedIn,
  markLive,
  showBetWithPlayUp,
  campaignButtonTitle,
  userTotalBonus,
  ticketsCount = 0,
  onToggleCampaignModal,
  onClose,
  onTapContentLink,
  secondaryLinks
}: Props) {
  const dispatch = useDispatch();

  return (
    <div>
      <MenuGroup>
        <MobileMenuItem type="route" url="/lobby" onClick={onClose} icon="icon_menu_lobby">
          {t('Lobby')}
        </MobileMenuItem>
        {isSignedIn && (
          <MobileMenuItem
            type="route"
            url="/my-challenges"
            onClick={onClose}
            isLive={markLive}
            icon="icon_menu_my_challenges"
          >
            {t('My Challenges')}
          </MobileMenuItem>
        )}
        {isSignedIn && displayMyTeams && (
          <MobileMenuItem
            type="route"
            url="/my-teams"
            onClick={onClose}
            isLive={markLive}
            icon="icon_menu_my_teams"
          >
            {t('My Teams')}
          </MobileMenuItem>
        )}
        <MobileMenuItem
          type="route"
          url="/live-challenges"
          onClick={onClose}
          icon="icon_menu_live_now"
        >
          {t('Live Now')}
        </MobileMenuItem>
        {isSignedIn && displayComboCalculator && (
          <MobileMenuItem
            type="route"
            url="/combo-calculator"
            onClick={onClose}
            icon="icon-sport-horse"
          >
            {t('Combo Calculator')}
          </MobileMenuItem>
        )}
        {isSignedIn && displayFantasyInsider && (
          <MobileMenuItem
            type="route"
            icon="icon_menu_my_teams"
            onClick={onClose}
            url="/fantasy-insider"
            isFree
          >
            Fantasy Insider
          </MobileMenuItem>
        )}
        {displayRapidFire && (
          <MobileMenuItem
            type="route"
            url="/rapidfire"
            onClick={onClose}
            icon="icon_menu_rapidfire"
          >
            RapidFire
          </MobileMenuItem>
        )}
        {isSignedIn && (
          <MobileMenuItem
            type="route"
            onClick={onClose}
            url="/account/bonus"
            icon="icon-menu-bonus"
          >
            Bonus{' '}
            <UserBonusAmount>
              {' '}
              {userTotalBonus !== undefined && `$${numeral(userTotalBonus).format('0,0.00')}`}
              {ticketsCount > 0 && (
                <NotificationDot>
                  <span>{ticketsCount}</span>
                </NotificationDot>
              )}
            </UserBonusAmount>
          </MobileMenuItem>
        )}
      </MenuGroup>
      <MenuGroup>
        {isSignedIn && displayFriends && (
          <MobileMenuItem
            type="route"
            url="/groups/friends"
            onClick={onClose}
            icon="icon_menu_friends"
          >
            {t('Friends')}
          </MobileMenuItem>
        )}
        {isSignedIn && (
          <MobileMenuItem
            onClick={() => {
              dispatch(toggleChat());
              onClose();
            }}
            icon="icon-chat"
            type="modal"
          >
            {t('Chat')}
          </MobileMenuItem>
        )}
        {displayGlobalLeaderboard && (
          <MobileMenuItem
            type="route"
            url="/ultimate-champion"
            onClick={onClose}
            icon="icon_menu_ultimate_champion"
          >
            {t('Ultimate Champion')}
          </MobileMenuItem>
        )}
        {displayGiftStore && (
          <MobileMenuItem
            type="route"
            url="/gift/store"
            onClick={onClose}
            icon="icon_menu_gift_store"
          >
            {t(giftStoreName)}
          </MobileMenuItem>
        )}
        {blogLink && (
          <MobileMenuItem type="external" url={blogLink} onClick={onClose} icon="icon_menu_news">
            {t('News & Tips')}
          </MobileMenuItem>
        )}
        {Array.isArray(externalLinks) &&
          externalLinks.length > 0 &&
          externalLinks.map(({ url, label, icon }) => (
            <MobileMenuItem key={label} type="external" url={url} onClick={onClose} icon={icon}>
              {t(label)}
            </MobileMenuItem>
          ))}
        {displayTraining && (
          <MobileMenuItem
            type="route"
            url="/training"
            onClick={onClose}
            icon="icon_menu_training_ground"
          >
            {t('Training Ground')}
          </MobileMenuItem>
        )}
      </MenuGroup>
      <MenuGroup>
        {secondaryLinks.map((item, i) => (
          <MobileMenuItem
            key={i} // eslint-disable-line react/no-array-index-key
            icon={item.icon}
            onClick={item.type === 'modal' ? onTapContentLink : onClose}
            type={item.type}
            url={item.href}
          >
            {t(item.name)}
          </MobileMenuItem>
        ))}
      </MenuGroup>
    </div>
  );
}

function mapStateToProps(state: GlobalState) {
  const credits = get('currentUser.bonusPack.bonuses', state.user) || [];
  const ticketsCount = credits ? filter(credit => credit.type === TICKET)(credits).length : 0;
  return {
    avatar: get('avatar', state.user.currentUser),
    hasPlayUpCampaign: !!get('hasPlayUpCampaign', state.user.currentUser),
    campaignButtonTitle: get('campaignButtonTitle', state.user.currentUser),
    showBetWithPlayUp: !!get('showBetWithPlayUp', state.user.currentUser),
    userTotalBonus: get('bonusPack.totalBalance', state.user.currentUser),
    ticketsCount,
    blogLink: state.settings.blogUrl,
    externalLinks: state.settings.externalLinks || [],
    displayComboCalculator: state.settings.displayComboCalculator,
    displayFantasyInsider: state.settings.displayFantasyInsider,
    displayFriends: state.settings.displayFriends,
    displayGlobalLeaderboard: state.settings.displayGlobalLeaderboard,
    displayGiftStore: state.settings.displayGiftStore,
    displayMyTeams: state.settings.displayMyTeams,
    displayTraining: state.settings.displayTraining,
    displayRapidFire: state.settings.displayRapidFire,
    giftStoreName: state.settings.giftStoreName,
    hideLogin: state.settings.hideLogin,
    isOpen: state.ui.isMobileMenuOpen,
    isSignedIn: state.user.isSignedIn,
    logoImage: state.settings.logoImage,
    markLive: some({ statusId: IN_PROGRESS }, values(state.myChallenges.live.challenges)),
    secondaryLinks: secondaryLinksSelector(state),
    userName: get('name', state.user.currentUser)
  };
}

const actions = {
  onToggleCampaignModal: toggleCampaignModal
};

export default connect(mapStateToProps, actions)(MobileMenuList);

const UserBonusAmount = styled.span`
  position: relative;
`;

const NotificationDot = styled.div(({ theme }) => ({
  border: '1px solid',
  borderRadius: '50%',
  position: 'absolute',
  top: -10,
  right: -15,
  height: 17,
  width: 17,
  textAlign: 'center',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: theme.color.primary,
  borderColor: theme.color.primary,
  color: theme.color.white,
  fontSize: '0.7rem',
  fontWeight: theme.fontWeight.bold
}));
